import { Link, useLocation, useRouter } from "@tanstack/react-router";
import { ChevronsUpDown, FolderKanban, Home, LogOut, Settings, Users } from "lucide-react";
import { FlotionMark, FlotionWordmark } from "@/components/brand/FlotionLogo";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { signOut, useSession } from "@/lib/auth/client";

export function AppSidebar() {
  const { data: session } = useSession();
  const router = useRouter();
  const location = useLocation();
  const { state } = useSidebar();

  const isAdmin = session?.user?.role === "admin";

  // Section-aware active state for the two dashboard entries.
  const dashSearch = location.search as { campaign?: string; section?: string };
  const onDashboard = location.pathname === "/dashboard";
  const inCampaigns = Boolean(dashSearch.campaign) || dashSearch.section === "campaigns";
  const homeActive = onDashboard && !inCampaigns;
  const campaignsActive = onDashboard && inCampaigns;

  function isActive(href: string) {
    return location.pathname.startsWith(href);
  }

  async function handleSignOut() {
    await signOut();
    router.navigate({ to: "/login" });
  }

  const userInitial = session?.user?.name?.charAt(0).toUpperCase() ?? "U";

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            {state === "collapsed" ? (
              <SidebarTrigger className="size-8 text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground" />
            ) : (
              <SidebarMenuButton size="lg">
                <FlotionMark size={22} />
                <FlotionWordmark height={18} className="flex-1 text-sidebar-foreground" />
                <SidebarTrigger className="size-7 text-sidebar-foreground/60 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground" />
              </SidebarMenuButton>
            )}
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={homeActive} tooltip="Home">
                  <Link to="/dashboard" search={{ section: "home" }}>
                    <Home className={homeActive ? "text-white" : "text-sidebar-foreground/50"} />
                    <span>Home</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild isActive={campaignsActive} tooltip="Campaigns">
                  <Link to="/dashboard" search={(prev) => ({ ...prev, section: "campaigns" })}>
                    <FolderKanban
                      className={campaignsActive ? "text-white" : "text-sidebar-foreground/50"}
                    />
                    <span>Campaigns</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="data-open:bg-sidebar-accent data-open:text-sidebar-accent-foreground"
                  tooltip={session?.user?.name ?? "User"}
                >
                  <Avatar className="size-8 rounded-lg">
                    <AvatarFallback className="rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                      {userInitial}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">{session?.user?.name ?? "User"}</span>
                    <span className="truncate text-xs text-sidebar-foreground/50">
                      {session?.user?.email ?? ""}
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-auto" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg"
                side="top"
                align="start"
                sideOffset={4}
              >
                <DropdownMenuItem asChild>
                  <Link to="/dashboard/team">
                    <Users />
                    Team
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleSignOut}>
                  <LogOut />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
          {isAdmin && (
            <>
              <SidebarSeparator />
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={isActive("/dashboard/settings")}
                  tooltip="Settings"
                >
                  <Link to="/dashboard/settings">
                    <Settings
                      className={
                        isActive("/dashboard/settings")
                          ? "text-white"
                          : "text-sidebar-foreground/50"
                      }
                    />
                    <span>Settings</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </>
          )}
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
